import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  CampaignSummaryItem,
  getCampaignStaffingStatus,
  sortCampaignsByOperationalPriority,
} from '../../utils/operationsDashboardUtils';
import {
  buildStaffingBarSegments,
  getCampaignHealthBadge,
  getCampaignStaffingFraction,
} from '../../utils/compactDashboardUtils';
import { getStaffingProgressColor, OPS_COLORS } from '../../constants/operationsDashboardColors';

interface CampaignPerformanceStripProps {
  campaigns: CampaignSummaryItem[];
  selectedCampaignId: string;
  onSelectCampaign: (campaignId: string) => void;
}

const CampaignPerformanceStrip: React.FC<CampaignPerformanceStripProps> = ({
  campaigns,
  selectedCampaignId,
  onSelectCampaign,
}) => {
  const sorted = sortCampaignsByOperationalPriority(campaigns);

  if (sorted.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 8,
        mb: 0.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          overflowX: 'auto',
          px: 0.75,
          py: 0.5,
          '&::-webkit-scrollbar': { height: 5 },
        }}
      >
        {sorted.map((campaign) => {
          const { working, required, percent, shortageLabel } = getCampaignStaffingFraction(campaign);
          const healthBadge = getCampaignHealthBadge(campaign);
          const staffing = getCampaignStaffingStatus(campaign);
          const selected = selectedCampaignId === campaign.campaignId;
          const barColor = getStaffingProgressColor(percent);
          const bar = buildStaffingBarSegments(working, required, 8);

          return (
            <Box
              key={campaign.campaignId}
              onClick={() => onSelectCampaign(campaign.campaignId)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onSelectCampaign(campaign.campaignId);
              }}
              sx={{
                flex: '0 0 auto',
                width: 148,
                px: 0.75,
                py: 0.5,
                border: '1px solid',
                borderColor: selected ? barColor : 'divider',
                borderLeftWidth: 3,
                borderLeftColor: barColor,
                borderRadius: 0.75,
                bgcolor: selected ? `${barColor}10` : 'background.paper',
                cursor: 'pointer',
                '&:hover': { bgcolor: `${barColor}08` },
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                noWrap
                sx={{ fontSize: '0.68rem', display: 'block', lineHeight: 1.2 }}
                title={campaign.campaignName}
              >
                {campaign.campaignName}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.3 }}>
                {working} / {required}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  lineHeight: 1.2,
                  color: staffing.openPositions > 0 ? OPS_COLORS.uncovered : OPS_COLORS.present,
                  fontWeight: 600,
                }}
              >
                {shortageLabel}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.55rem',
                  letterSpacing: 0.5,
                  color: barColor,
                  display: 'block',
                  lineHeight: 1,
                }}
              >
                {bar}
              </Typography>
              <Box
                component="span"
                sx={{
                  mt: 0.25,
                  display: 'inline-block',
                  px: 0.5,
                  py: 0.1,
                  borderRadius: 0.5,
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  bgcolor: healthBadge.bgcolor,
                  color: healthBadge.color,
                }}
              >
                {healthBadge.label}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CampaignPerformanceStrip;
